import { createScreenText } from 'helpers/screenText';
const st = createScreenText('reports_issuance_reporting');

// src/pages/Reports/IssuanceReporting/shared/types.ts
// README.md mục 7 — toàn bộ interface DTO cho 18 báo cáo phát hành vé số + danh mục Nhà in +
// 2 workflow xác nhận (Đối soát đại lý, Chốt kỳ). Gom 1 chỗ vì cả module IssuanceReporting đều
// dùng chung, tách khỏi src/pages/Reports/types.ts cũ (6 báo cáo cũ, không đụng vào).
// Field/kiểu dữ liệu lấy đúng theo DTO thực tế bên ticket-service (TicketService.Contacts.Dtos).

// ---------------------------------------------------------------------------
// RPT-001 — Tổng hợp phát hành vé (1 dòng = 1 (IssuancePlan, Symbol))
// ---------------------------------------------------------------------------
export interface IssuanceSummaryReportRow {
  issuancePlanId: number;
  planCode: string;
  ticketType: string;
  ticketTypeName?: string | null;
  symbol: string;
  rangeCount: number;
  totalQuantity: number;
  totalValue: number;
  printedQuantity: number;
  warehousedQuantity: number;
  deliveredQuantity: number;
  remainingQuantity: number;
  recalledQuantity: number;
  cancelledQuantity: number;
  discrepancyQuantity: number;
  status: number;
  statusName: string;
}

// RPT-017 — Tổng hợp toàn bộ 1 kỳ xổ (không tách theo Symbol)
export interface IssuancePeriodSummaryReport {
  issuancePlanId: number;
  planCode: string;
  expectedDrawDate: string;
  totalIssuedQuantity: number;
  totalWarehousedQuantity: number;
  totalDeliveredQuantity: number;
  totalSoldQuantity: number;
  totalRecalledQuantity: number;
  totalRemainingQuantity: number;
  totalCancelledQuantity: number;
  totalDiscrepancyQuantity: number;
  totalIssuedValue: number;
  totalSoldValue: number;
  totalRecalledValue: number;
  consumptionRate: number;
  status: number;
  statusName: string;
}

// ---------------------------------------------------------------------------
// RPT-002 — Chi tiết phát hành theo dải
// ---------------------------------------------------------------------------
export interface TicketIssuanceRangeReportRow {
  issuancePlanId: number;
  planCode: string;
  ticketType: string;
  ticketTypeName?: string | null;
  symbol: string;
  rangeStart?: string | null;
  rangeEnd?: string | null;
  quantity: number;
  value: number;
  // "DaGiao" | "DaThuHoi" | "ConKho"
  status: string;
  statusName: string;
  issuedDate?: string | null;
  warehouseLabel: string;
}

// ---------------------------------------------------------------------------
// RPT-003 / RPT-013 — Nhập vé từ nhà in / Tổng hợp theo nhà in
// ---------------------------------------------------------------------------
export interface PrintHouseReceiptReportRow {
  id: number;
  issuancePlanId: number;
  planCode: string;
  printHouseId?: number | null;
  printHouseName: string;
  warehouseLabel: string;
  ticketType: string;
  ticketTypeName?: string | null;
  rangeStart?: string | null;
  rangeEnd?: string | null;
  expectedQuantity?: number | null;
  actualReceivedQuantity?: number | null;
  shortageQuantity: number;
  errorCount: number;
  value: number;
  receivedBy?: number | null;
  receivedAt?: string | null;
  // "ChoNhan" | "DaNhan"
  status: string;
  statusName: string;
}

export interface PrintHouseSummaryReportRow {
  printHouseId?: number | null;
  printHouseName: string;
  issuancePlanId: number;
  planCode: string;
  ticketType: string;
  ticketTypeName?: string | null;
  expectedQuantity?: number | null;
  deliveredQuantity?: number | null;
  actualReceivedQuantity?: number | null;
  shortageQuantity: number;
  errorCount: number;
  value: number;
  completedAt?: string | null;
  status: string;
  statusName: string;
}

// ---------------------------------------------------------------------------
// RPT-004 — Tồn kho vé (kho tổng)
// ---------------------------------------------------------------------------
export interface TicketStockReportRow {
  issuancePlanId: number;
  planCode: string;
  ticketType: string;
  ticketTypeName?: string | null;
  symbol: string;
  warehouseLabel: string;
  quantity: number;
  value: number;
  lastUpdatedAt?: string | null;
}

// ---------------------------------------------------------------------------
// RPT-005 — Phân phối vé (tổng hợp theo 1 phiếu giao)
// ---------------------------------------------------------------------------
export interface TicketDistributionReportRow {
  deliveryId: number;
  deliveryCode: string;
  issuancePlanId: number;
  planCode: string;
  warehouseLabel: string;
  agentId: number;
  agentName?: string | null;
  ticketType: string;
  ticketTypeName?: string | null;
  symbols: string;
  lineCount: number;
  totalQuantity: number;
  totalValue: number;
  deliveredAt?: string | null;
  deliveredBy?: number | null;
  deliveredByName?: string | null;
  status: number;
  statusName: string;
}

// ---------------------------------------------------------------------------
// RPT-006 — Chi tiết giao vé cho đại lý (1 dòng = 1 dải trong 1 phiếu giao)
// ---------------------------------------------------------------------------
export interface TicketDeliveryLineReportRow {
  deliveryId: number;
  deliveryCode: string;
  issuancePlanId: number;
  planCode: string;
  agentId: number;
  agentName?: string | null;
  warehouseLabel: string;
  ticketType: string;
  ticketTypeName?: string | null;
  symbol: string;
  rangeStart: string;
  rangeEnd: string;
  quantity: number;
  value: number;
  deliveredAt?: string | null;
  deliveredBy?: number | null;
  deliveredByName?: string | null;
  status: number;
  statusName: string;
}

// ---------------------------------------------------------------------------
// RPT-007 — Thu hồi vé (tổng hợp theo 1 phiếu thu hồi)
// ---------------------------------------------------------------------------
export interface TicketRecallSummaryReportRow {
  recallId: number;
  recallCode: string;
  issuancePlanId: number;
  planCode: string;
  agentId?: number | null;
  agentName?: string | null;
  warehouseLabel: string;
  ticketType: string;
  ticketTypeName?: string | null;
  symbols: string;
  lineCount: number;
  expectedQuantity: number;
  actualQuantity: number;
  discrepancyQuantity: number;
  value: number;
  confirmedAt?: string | null;
  createdBy?: number | null;
  confirmedBy?: number | null;
  status: number;
  statusName: string;
}

// ---------------------------------------------------------------------------
// RPT-008 — Chi tiết vé thu hồi (1 dòng = 1 dải trong 1 phiếu thu hồi)
// ---------------------------------------------------------------------------
export interface TicketRecallLineReportRow {
  recallId: number;
  recallCode: string;
  issuancePlanId: number;
  planCode: string;
  agentId?: number | null;
  agentName?: string | null;
  warehouseLabel: string;
  ticketType: string;
  ticketTypeName?: string | null;
  symbol: string;
  rangeStart: string;
  rangeEnd: string;
  deliveredQuantity: number;
  soldQuantity: number;
  expectedQuantity: number;
  actualQuantity: number;
  discrepancyQuantity: number;
  value: number;
  confirmedAt?: string | null;
  status: number;
  statusName: string;
}

// ---------------------------------------------------------------------------
// RPT-011 — Vé đã bán (suy diễn = Đã giao − Thu hồi)
// ---------------------------------------------------------------------------
export interface TicketSoldReportRow {
  issuancePlanId: number;
  planCode: string;
  agentId: number;
  agentName?: string | null;
  ticketType: string;
  ticketTypeName?: string | null;
  symbol: string;
  soldQuantity: number;
  soldValue: number;
  lastDeliveredAt?: string | null;
}

// ---------------------------------------------------------------------------
// RPT-012 — Tổng hợp theo đại lý (gộp mọi kỳ phát hành)
// ---------------------------------------------------------------------------
export interface AgentSummaryReportRow {
  agentId: number;
  agentName?: string | null;
  periodCount: number;
  totalDeliveredQuantity: number;
  totalSoldQuantity: number;
  totalRecalledQuantity: number;
  totalDiscrepancyQuantity: number;
  totalDeliveredValue: number;
  totalSoldValue: number;
  totalRecalledValue: number;
  consumptionRate: number;
  recallRate: number;
  reconciledPeriodCount: number;
  // Bổ sung — HH bán hàng (giảm khi thu hồi vé ế) + HH trả thưởng (giảm khi thu hồi giải thưởng).
  // % lấy từ cấu hình hệ thống; số tiền do backend tính sẵn.
  salesCommissionRate: number;
  salesCommission: number;
  prizePayoutCommissionRate: number;
  prizePayoutCommission: number;
}

// ---------------------------------------------------------------------------
// RPT-015 / RPT-016 — Tra cứu 1 vé / Tra cứu 1 dải vé
// ---------------------------------------------------------------------------
export interface TicketLookupReport {
  issuancePlanId: number;
  planCode: string;
  ticketType: string;
  ticketTypeName?: string | null;
  symbol: string;
  ticketNumber: string;
  warehouseLabel: string;
  // "ConKho" | "DaGiao" | "DaThuHoi" | "DaHuy"
  currentState: string;
  currentStateName: string;
  agentId?: number | null;
  agentName?: string | null;
  expectedDrawDate?: string | null;
  deliveredAt?: string | null;
  recalledAt?: string | null;
  cancelledAt?: string | null;
  // URL tra cứu in trên vé (LotteryTicket.QrUrl) — dùng render ảnh QR bên cạnh kết quả tra cứu.
  qrUrl?: string | null;
}

export interface TicketRangeLookupSegment {
  rangeStart: string;
  rangeEnd: string;
  quantity: number;
  // "ConKho" | "DaGiao" | "DaThuHoi"
  currentState: string;
  currentStateName: string;
  agentId?: number | null;
  agentName?: string | null;
  referenceCode?: string | null;
  eventAt?: string | null;
}

export interface TicketRangeLookupResult {
  issuancePlanId: number;
  planCode: string;
  symbol: string;
  rangeStart: string;
  rangeEnd: string;
  totalQuantity: number;
  segments: TicketRangeLookupSegment[];
}

// ---------------------------------------------------------------------------
// RPT-009 — Đối soát đại lý (có workflow xác nhận)
// ---------------------------------------------------------------------------
export interface AgentReconciliation {
  id: number;
  issuancePlanId: number;
  planCode: string;
  agentId: number;
  agentName?: string | null;
  status: number;
  statusName: string;
  deliveredQuantity: number;
  recalledQuantity: number;
  soldQuantity: number;
  discrepancyQuantity: number;
  discrepancyReason?: string | null;
  note?: string | null;
  consumptionRate: number;
  confirmedBy?: number | null;
  confirmedAt?: string | null;
}

export interface ConfirmAgentReconciliationPayload {
  issuancePlanId: number;
  agentId: number;
  discrepancyQuantity: number;
  discrepancyReason?: string | null;
  note?: string | null;
}

// ---------------------------------------------------------------------------
// RPT-010 — Chênh lệch (có ghi nhận thủ công + xử lý)
// ---------------------------------------------------------------------------
export interface TicketDiscrepancy {
  id: number;
  issuancePlanId: number;
  planCode: string;
  discrepancyType: number;
  discrepancyTypeName: string;
  referenceType?: string | null;
  referenceId?: number | null;
  agentId?: number | null;
  agentName?: string | null;
  symbol?: string | null;
  rangeStart?: string | null;
  rangeEnd?: string | null;
  expectedQuantity: number;
  actualQuantity: number;
  discrepancyQuantity: number;
  discrepancyValue: number;
  reason?: string | null;
  status: number;
  statusName: string;
  resolvedBy?: number | null;
  resolvedAt?: string | null;
  resolutionNote?: string | null;
  createdBy: number;
  createdAt: string;
}

export interface CreateTicketDiscrepancyPayload {
  issuancePlanId: number;
  discrepancyType: number;
  agentId?: number | null;
  symbol?: string | null;
  rangeStart?: string | null;
  rangeEnd?: string | null;
  expectedQuantity: number;
  actualQuantity: number;
  reason: string;
}

export interface ResolveTicketDiscrepancyPayload {
  resolutionNote: string;
}

// 3 loại chênh lệch cho phép ghi nhận thủ công qua UI — "Lệch khi đối soát" (giá trị 4) chỉ
// BE tự tạo từ ConfirmAgentReconciliationCommand, không cho chọn tay ở đây.
export const DISCREPANCY_TYPE_OPTIONS = [
  { value: 1, label: st('thieu_khi_nhap_kho_label') },
  { value: 2, label: st('lech_khi_giao_ve_label') },
  { value: 3, label: st('lech_khi_thu_hoi_label') },
] as const;

// ---------------------------------------------------------------------------
// RPT-014 — Vé hủy (chỉ đọc, auto-log từ BE)
// ---------------------------------------------------------------------------
export interface TicketCancellation {
  id: number;
  issuancePlanId: number;
  planCode: string;
  symbol?: string | null;
  rangeStart?: string | null;
  rangeEnd?: string | null;
  quantity: number;
  value: number;
  reason: string;
  requestedBy: number;
  requestedAt: string;
  approvedBy?: number | null;
  approvedAt?: string | null;
  status: number;
  statusName: string;
  referenceType: string;
  referenceId: number;
}

// ---------------------------------------------------------------------------
// RPT-018 — Chốt kỳ (có workflow chốt)
// ---------------------------------------------------------------------------
export interface PeriodClosing {
  id: number;
  issuancePlanId: number;
  planCode: string;
  status: number;
  statusName: string;
  totalIssuedQuantity: number;
  totalWarehousedQuantity: number;
  totalDeliveredQuantity: number;
  totalSoldQuantity: number;
  totalRecalledQuantity: number;
  totalRemainingQuantity: number;
  totalCancelledQuantity: number;
  totalDiscrepancyQuantity: number;
  totalIssuedValue: number;
  totalSoldValue: number;
  totalRecalledValue: number;
  agentsReconciledCount: number;
  agentsNotReconciledCount: number;
  agentsWithDiscrepancyCount: number;
  forceClose: boolean;
  forceCloseReason?: string | null;
  closedBy?: number | null;
  closedAt?: string | null;
}

export interface ClosePeriodPayload {
  issuancePlanId: number;
  forceClose: boolean;
  forceCloseReason?: string | null;
}

// ---------------------------------------------------------------------------
// Danh mục Nhà in
// ---------------------------------------------------------------------------
export interface PrintHouse {
  id: number;
  code: string;
  name: string;
  address: string;
  contactName?: string | null;
  contactPhone?: string | null;
  note?: string | null;
  isActive: boolean;
  createdAt: string;
  lastModifiedAt?: string | null;
}

export interface CreatePrintHousePayload {
  code: string;
  name: string;
  address: string;
  contactName?: string | null;
  contactPhone?: string | null;
  note?: string | null;
}

export interface UpdatePrintHousePayload {
  id: number;
  name: string;
  address: string;
  contactName?: string | null;
  contactPhone?: string | null;
  note?: string | null;
  isActive: boolean;
}

// ---------------------------------------------------------------------------
// Phân trang dùng chung — khớp shape PaginatedList<T> của BuildingBlocks.Paginated
// (Items/TotalItems/PageIndex/PageSize theo README_RULE.md/pagination-rules.md).
// ---------------------------------------------------------------------------
export interface ReportPaginationState {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export const DEFAULT_PAGE_SIZE = 10;

export const INITIAL_REPORT_PAGINATION: ReportPaginationState = {
  currentPage: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  totalItems: 0,
  totalPages: 0,
};
